import { Button } from "@/components/Button";
import PersonProfile from "@/components/PersonProfile";
import { ChevronRightIcon, CheckIcon, ChevronDownIcon } from "@heroicons/react/24/solid";
import { useState } from "react";

export const gradientGreen = "bg-gradient-to-r from-[#ced812] to-[#80af28]";


interface InfoBoxProps {
    children: React.ReactNode;
    className?: string;
}

const InfoBox = ({ children, className = "" }: InfoBoxProps) => {
    const background = "bg-[#08082f]";
    const borderVerdeOliva = "border-[#d2de00]";

    return (
        <div className={`max-w-[1100px] ${background} text-white text-[24.9px] font-semibold border ${borderVerdeOliva} border-4 p-8 rounded-[16px] space-y-4 ${className}`}>
            {children}
        </div>
    );
}

interface FAQItemProps {
    question: string;
    answer: string;
    isOpen: boolean;
    onToggle: () => void;
}

const FAQItem = ({ question, answer, isOpen, onToggle }: FAQItemProps) => {
    return (
        <div className="bg-white rounded-xl shadow-lg border-l-4 border-[#d2de00] overflow-hidden">
            <button
                className="w-full p-6 text-left hover:bg-gray-50 transition-colors duration-200 flex justify-between items-center"
                onClick={onToggle}
            >
                <h3 className="text-lg font-bold text-[#08082f] pr-4">
                    {question}
                </h3>
                <ChevronDownIcon
                    className={`w-5 h-5 text-[#d2de00] flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'
                        }`}
                />
            </button>
            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}>
                <div className="px-6 pb-6">
                    <p className="text-gray-700 leading-relaxed">
                        {answer}
                    </p>
                </div>
            </div>
        </div>
    );
}


const Standard: React.FC = () => {
    const [openFAQs, setOpenFAQs] = useState<number[]>([]);

    const background = "bg-[#08082f]";
    const textVerdeOliva = "text-[#d2de00]";

    const goToPurchase = () => {
        window.open('https://teraquiz.com/asaas?id=questoes-flashcards-urologia-2025', '_blank');
    };

    const scrollToPricing = () => {
        const pricingSection = document.getElementById('pricing-section');
        if (pricingSection) {
            pricingSection.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const toggleFAQ = (index: number) => {
        setOpenFAQs(prev =>
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    const faqData = [
        {
            question: "1. As questões são inéditas ou retiradas de provas anteriores?",
            answer: "O banco reúne questões inéditas e questões do Pro-TiSBU R1, R2 e R3 e TiSBU de anos anteriores, todas atualizadas e com comentários completos."
        },
        {
            question: "2. Quem elaborou o conteúdo?",
            answer: "O material foi criado por especialistas em urologia formados pela USP e aprovados no TiSBU, garantindo alinhamento com o padrão de cobrança da prova."
        },
        {
            question: "3. As questões seguem quais referências?",
            answer: "Todas são comentadas de acordo com as EAU Guidelines 2025, AUA Guidelines e Campbell-Walsh, oferecendo segurança e atualização."
        },
        {
            question: "4. Como recebo o acesso após a compra?",
            answer: "O acesso é 100% online e imediato — assim que o pagamento é confirmado, você recebe seu login e senha para estudar onde e quando quiser."
        },
        {
            question: "5. Posso acessar pelo celular?",
            answer: "Sim. A plataforma é responsiva e funciona perfeitamente no celular, tablet ou computador."
        },
        {
            question: "6. Quanto tempo terei acesso ao banco de questões?",
            answer: "O acesso é válido por 12 meses a partir da data da compra."
        },
        {
            question: "7. Esse material substitui meus livros e apostilas?",
            answer: "Não substitui, mas potencializa seus estudos. As questões funcionam como um treino ativo e revisão rápida, complementando o estudo teórico."
        },
        {
            question: "8. Há garantia de satisfação?",
            answer: "Sim. Você tem 7 dias para testar o banco de questões. Se não estiver satisfeito, devolvemos seu dinheiro sem burocracia."
        },
        {
            question: "9. O banco simula a prova real?",
            answer: "Sim. Há um modo simulado que replica o formato e o tempo da prova TiSBU, além do modo de treino para estudar questão por questão com comentários."
        }
    ];

    return (
        <div className={`flex flex-col ${background} w-full h-full items-center justify-center`}>
            <div className="py-16 px-4 md:px-8 w-full">
                <div className="max-w-7xl mx-auto text-center flex flex-col items-center">
                    <img className="h-[54px] mb-4" src="https://teraquiz.s3.us-east-005.backblazeb2.com/teraquiz/image_1756492433855137955_68b1f291.webp"></img>

                    <p className={`${textVerdeOliva} mb-8 text-base md:text-lg lg:text-xl`}>O único preparatório baseado 100% nas questões do TISBU</p>
                    <div className="mb-16">
                        <img
                            className="h-32 md:h-40 mx-auto rounded-2xl shadow-2xl"
                            src="https://teraquiz.s3.us-east-005.backblazeb2.com/teraquiz/image_1756386759089154743_68b055c7.webp"
                            alt="Teraquiz Platform"
                        />
                    </div>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-[52.8px] max-w-6xl mx-auto text-center text-white font-bold leading-tight">
                        Conquiste sua
                        <span className={`${textVerdeOliva} bg-gradient-to-r from-[#d2de00] to-[#a8b600] bg-clip-text text-transparent`}> aprovação </span>
                        na prova de título de Urologia
                    </h2>
                    <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed">
                        com nosso Curso + Banco de Questões Premium
                    </p>

                    <div className="max-w-[800px] mx-auto mt-16 text-white font-light text-lg md:text-xl lg:text-[21.5px] leading-[1.2] text-center">
                        <p>
                            Banco de Questões TiSBU + Flashcards + Curso Intensivo (como bônus no lançamento!)
                        </p>
                        <br />
                        <p>
                            Curso de 8 horas focado na prova + <strong className="font-semibold">1000 Questões Comentadas para Aprovação + 1000 Flashcards + crie seus flashcards</strong>
                        </p>
                        <p className={`mb-8 ${textVerdeOliva} font-semibold`}>
                            A preparação mais completa e estratégica para as provas R1, R2 e R3.
                        </p>
                        <Button className="mb-8" onClick={scrollToPricing}>QUERO MINHA APROVAÇÃO</Button>
                    </div>
                </div>
                <div className="py-20 px-4 md:px-8 w-full">
                    <div className="max-w-7xl mx-auto">
                        <h2 className="text-3xl md:text-2xl lg:text-3xl font-bold text-white mb-2 leading-tight">
                            <span className={`${textVerdeOliva}`}>CURSO INTENSIVO TiSBU</span>
                            <br />
                            <span className="text-white">+ BANCO DE QUESTÕES</span>
                        </h2>
                        <p className="text-white text-[14px] mb-6">(LANÇAMENTO CURSO 06/10 + BANCO DE QUESTÕES ACESSO IMEDIATO)</p>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            <div className="order-1 lg:order-1">
                                <div className="space-y-6">
                                    <div className="bg-gradient-to-r from-gray-900/50 to-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:border-[#d2de00]/50 transition-all duration-300 group">
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 bg-gradient-to-br from-[#d2de00]/20 to-[#a8b600]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                                                <ChevronRightIcon className={`w-6 h-6 ${textVerdeOliva} group-hover:scale-110 transition-transform duration-300`} />
                                            </div>
                                            <div>
                                                <h3 className="text-white font-bold text-lg mb-2">Pontos-Chave Estratégicos</h3>
                                                <p className="text-gray-300 leading-relaxed">
                                                    Cada tema da urologia com maior recorrência em provas, sem perda de tempo
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-gradient-to-r from-gray-900/50 to-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:border-[#d2de00]/50 transition-all duration-300 group">
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 bg-gradient-to-br from-[#d2de00]/20 to-[#a8b600]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                                                <ChevronRightIcon className={`w-6 h-6 ${textVerdeOliva} group-hover:scale-110 transition-transform duration-300`} />
                                            </div>
                                            <div>
                                                <h3 className="text-white font-bold text-lg mb-2">Guidelines Atualizados</h3>
                                                <p className="text-gray-300 leading-relaxed">
                                                    Recomendações práticas dos guidelines que embasam a banca examinadora
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-gradient-to-r from-gray-900/50 to-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:border-[#d2de00]/50 transition-all duration-300 group">
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 bg-gradient-to-br from-[#d2de00]/20 to-[#a8b600]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                                                <ChevronRightIcon className={`w-6 h-6 ${textVerdeOliva} group-hover:scale-110 transition-transform duration-300`} />
                                            </div>
                                            <div>
                                                <h3 className="text-white font-bold text-lg mb-2">Questões Reais</h3>
                                                <p className="text-gray-300 leading-relaxed">
                                                    Discussão orientada por questões reais já cobradas em provas anteriores
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-gradient-to-r from-gray-900/50 to-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:border-[#d2de00]/50 transition-all duration-300 group">
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 bg-gradient-to-br from-[#d2de00]/20 to-[#a8b600]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                                                <ChevronRightIcon className={`w-6 h-6 ${textVerdeOliva} group-hover:scale-110 transition-transform duration-300`} />
                                            </div>
                                            <div>
                                                <h3 className="text-white font-bold text-lg mb-2">Estratégias de Prova</h3>
                                                <p className="text-gray-300 leading-relaxed">
                                                    Técnicas de prova e priorização de conteúdo para maximizar sua pontuação
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-10 text-center">
                                    <Button className="text-xl px-8 py-4" onClick={scrollToPricing}>QUERO MINHA APROVAÇÃO</Button>
                                </div>
                            </div>

                            <div className="order-2 lg:order-2 text-center">
                                <img
                                    className="w-full max-w-md mx-auto rounded-3xl shadow-2xl"
                                    src="https://teraquiz.s3.us-east-005.backblazeb2.com/teraquiz/image_1756472397612372700_68b1a44d.webp"
                                    alt="Curso TiSBU Interface"
                                />
                                <div className="mt-8 bg-gradient-to-r from-[#d2de00]/10 to-[#a8b600]/10 rounded-2xl p-6 border border-[#d2de00]/20">
                                    <p className={`${textVerdeOliva} font-bold text-lg mb-2`}>8 Horas de Conteúdo</p>
                                    <p className="text-gray-300">Focado exclusivamente no TiSBU</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-white w-full py-20 px-4 md:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold text-gray-800 mb-6 leading-tight">
                            Diferenciais do <span className="text-[#d2de00]">Banco de Questões</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                            Metodologia internacional aplicada à urologia brasileira
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="group bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 p-8 border border-gray-100 hover:border-[#d2de00]/30 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#d2de00] to-[#a8b600]"></div>
                            <div className="mb-6">
                                <img
                                    src="https://teraquiz.s3.us-east-005.backblazeb2.com/teraquiz/image_1756387706597927286_68b0597a.png"
                                    alt="QBank Method"
                                    className="w-20 h-20 mx-auto rounded-2xl shadow-lg border-2 border-gray-100"
                                />
                            </div>
                            <div className="text-center">
                                <h3 className="text-xl font-bold text-gray-800 mb-4">Método QBank Internacional</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    Baseadas no método <span className="font-bold text-[#f51f29] bg-[#f51f29]/10 px-2 py-1 rounded">QBank</span>,
                                    modelo consagrado internacionalmente em exames como
                                    <span className="font-bold text-[#f51f29] bg-[#f51f29]/10 px-2 py-1 rounded mx-1">USMLE</span>,
                                    garantindo estudo ativo e direcionado.
                                </p>
                            </div>
                        </div>

                        <div className="group bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 p-8 border border-gray-100 hover:border-[#d2de00]/30 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#d2de00] to-[#a8b600]"></div>
                            <div className="mb-6">
                                <img
                                    src="https://teraquiz.s3.us-east-005.backblazeb2.com/teraquiz/image_1756387663317554293_68b0594f.png"
                                    alt="Complete Comments"
                                    className="w-20 h-20 mx-auto rounded-2xl"
                                />
                            </div>
                            <div className="text-center">
                                <h3 className="text-xl font-bold text-gray-800 mb-4">Comentários Completos</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    Cada questão traz análise detalhada das alternativas corretas e incorretas, seguida de revisão aprofundada conforme
                                    <span className="font-bold text-[#f51f29] bg-[#f51f29]/10 px-2 py-1 rounded">EAU Guidelines 2025</span>,
                                    <span className="font-bold text-[#f51f29] bg-[#f51f29]/10 px-2 py-1 rounded">AUA Guidelines</span> e
                                    <span className="font-bold text-[#f51f29] bg-[#f51f29]/10 px-2 py-1 rounded">Campbell-Walsh</span>.
                                </p>
                            </div>
                        </div>

                        <div className="group bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 p-8 border border-gray-100 hover:border-[#d2de00]/30 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#d2de00] to-[#a8b600]"></div>
                            <div className="mb-6">
                                <img
                                    src="https://teraquiz.s3.us-east-005.backblazeb2.com/teraquiz/image_1756387688405814699_68b05968.png"
                                    alt="Clinical Reasoning"
                                    className="w-20 h-20 mx-auto rounded-2xl"
                                />
                            </div>
                            <div className="text-center">
                                <h3 className="text-xl font-bold text-gray-800 mb-4">Raciocínio Clínico Avançado</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    Foco no <span className="font-bold text-[#f51f29] bg-[#f51f29]/10 px-2 py-1 rounded">raciocínio clínico</span> e
                                    <span className="font-bold text-[#f51f29] bg-[#f51f29]/10 px-2 py-1 rounded">refinamento técnico</span>
                                    exigidos para alto desempenho. Linguagem clara com organização que permite
                                    <span className="font-bold text-[#f51f29] bg-[#f51f29]/10 px-2 py-1 rounded">revisão rápida e retenção eficiente</span>.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="mt-16 text-center">
                        <div className="bg-gradient-to-r from-[#d2de00]/10 to-[#a8b600]/10 rounded-2xl p-8 border border-[#d2de00]/20 max-w-4xl mx-auto">
                            <div className="flex items-center justify-center gap-4 mb-4">
                                <div className="w-3 h-3 bg-[#d2de00] rounded-full animate-pulse"></div>
                                <p className="text-2xl font-bold text-gray-800">1000 Questões Premium</p>
                                <div className="w-3 h-3 bg-[#d2de00] rounded-full animate-pulse"></div>
                            </div>
                            <p className="text-gray-600 text-lg">
                                Cada questão é uma oportunidade de aprendizado profundo e direcionado
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div className="bg-white w-full p-8 flex flex-col items-center">
                <p className="mb-4 text-[40.2px] font-bold mt-8">Diferenciais do curso:</p>
                <InfoBox>
                    <div className="flex items-start gap-3">
                        <CheckIcon className={`w-8 h-8 ${textVerdeOliva} mt-1 flex-shrink-0`} />
                        <p>
                            Único curso 100% direcionado ao que a banca cobra.
                        </p>
                    </div>
                    <div className="flex items-start gap-3">
                        <CheckIcon className={`w-8 h-8 ${textVerdeOliva} mt-1 flex-shrink-0`} />
                        <p className="font-light">
                            Construído a partir da análise das provas anteriores, sem desperdício de tempo em conteúdos irrelevantes.
                        </p>
                    </div>
                    <div className="flex items-start gap-3">
                        <CheckIcon className={`w-8 h-8 ${textVerdeOliva} mt-1 flex-shrink-0`} />
                        <p>
                            Professores formados nos melhores serviços do país e aprovados no TISBU.
                        </p>
                    </div>
                </InfoBox>

                <InfoBox className="mt-8 mb-12">
                    <p>
                        Todos os temas da prova são cobertos de forma <span className={textVerdeOliva}>CLARA e OBJETIVA</span>, deixando seu estudo direcionado e focado para a prova.
                    </p>
                    <p>
                        <span className={textVerdeOliva}>8 horas de aulas</span> com foco no que realmente cai no TISBU.
                    </p>
                </InfoBox>
            </div>
            <div className="py-20 px-4 md:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">

                        <h2 className="text-4xl md:text-6xl font-bold text-white leading-tight mb-6">
                            <span className={`bg-gradient-to-r from-[#d2de00] to-[#a8b600] bg-clip-text text-transparent`}>
                                Banco de Questões + Flashcards + Curso
                            </span>
                            <br />
                            <span className="text-white">para turbinar seu aprendizado</span>
                        </h2>
                        <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                            Acompanhe sua evolução com interface intuitiva e sistema de personalização completo.
                            Monte provas por tema, acompanhe estatísticas e potencialize seus resultados.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6 mb-12">
                        <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:border-[#d2de00]/50 transition-all duration-300 group">
                            <h3 className="text-lg font-bold text-white mb-3">Curso Completo</h3>
                            <p className="text-gray-300 text-sm leading-relaxed">
                                8 horas de aulas focadas no TiSBU + banco de questões comentadas por especialistas aprovados
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:border-[#d2de00]/50 transition-all duration-300 group">
                            <h3 className="text-lg font-bold text-white mb-3">Acesso Total</h3>
                            <p className="text-gray-300 text-sm leading-relaxed">
                                12 meses de acesso completo. Materiais de apoio inclusos: mapas mentais e resumos exclusivos
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:border-[#d2de00]/50 transition-all duration-300 group">
                            <h3 className="text-lg font-bold text-white mb-3">Questões Premium</h3>
                            <p className="text-gray-300 text-sm leading-relaxed">
                                1000 questões comentadas no estilo da prova, com análise detalhada de cada alternativa
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:border-[#d2de00]/50 transition-all duration-300 group">
                            <h3 className="text-lg font-bold text-white mb-3">App Exclusivo</h3>
                            <p className="text-gray-300 text-sm leading-relaxed">
                                Acesso pelo app Teraquiz. Estude onde e quando quiser, com sincronização em tempo real
                            </p>
                        </div>
                    </div>

                    <div className="flex justify-center my-12">
                        <img className="h-[400px] rounded-2xl shadow-2xl" src="https://teraquiz.s3.us-east-005.backblazeb2.com/teraquiz/image_1756394131613499720_68b07293.webp" alt="Teraquiz Features"></img>
                    </div>
                </div>
            </div>
            <Button className="mb-24" onClick={scrollToPricing}>QUERO MINHA APROVAÇÃO</Button>
            <div className={`${gradientGreen} w-full py-20 px-4 md:px-8`}>
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-6xl font-bold text-[#292929] mb-4 leading-tight">
                            Nosso Time de Excelência
                        </h2>
                        <p className="text-lg md:text-xl text-[#292929]/80 max-w-3xl mx-auto leading-relaxed">
                            Especialistas formados nas melhores instituições do país, todos aprovados no TiSBU, dedicados à sua aprovação
                        </p>
                        <div className="mt-8 flex justify-center">
                            <div className="bg-[#292929]/10 backdrop-blur-sm rounded-full px-6 py-3 border border-[#292929]/20">
                                <p className="text-[#292929] font-semibold">
                                    🏆 100% dos professores aprovados no TiSBU
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-20 md:gap-24 lg:gap-32 pb-24 md:pb-32 pt-28 md:pt-40">
                        <PersonProfile name="dra. ISABELA BARROS" photoUrl="https://teraquiz.s3.us-east-005.backblazeb2.com/teraquiz/image_1756394291595109705_68b07333.webp" description={<p>Médica pela Faculdade Pernambucana de Saúde
                            <br /><br />
                            Cirurgiã geral pela USP RP
                            <br /><br />
                            Urologista pela USP SP
                            <br /><br />
                            Observership em urologia funcional na Universidade de Sorbonne - Tenon Hospital
                            <br /><br />
                            Premiada pela European Surgical Academy</p>}>

                        </PersonProfile>
                        <PersonProfile name="dr. Henri MORGAN" photoUrl="https://teraquiz.s3.us-east-005.backblazeb2.com/teraquiz/image_1756394321726380337_68b07351.webp" description={<p>Médico pela Universidade Federal do Rio Grande
                            <br /><br />
                            Cirurgião Geral pela Escola Paulista de Medicina / UNIFESP
                            <br /><br />
                            Residente de Urologia USP-SP
                        </p>}>

                        </PersonProfile>
                        <PersonProfile name="dr. Gabriel Eufrásio" photoUrl="https://teraquiz.s3.us-east-005.backblazeb2.com/teraquiz/image_1756394346703030817_68b0736a.webp" description={<p>Médico pela Universidade de São Paulo (USP) – São Paulo
                            <br /><br />
                            Cirurgião Geral pela Universidade de São Paulo (USP) – São Paulo
                            <br /><br />
                            Urologista pela Universidade de São Paulo (USP) – São Paulo
                            <br /><br />
                            Aperfeiçoamento em Transplante Renal no Hospital das Clínicas da Faculdade de Medicina da USP
                            <br /><br />
                            Observership em Cirurgia Robótica na ORSI Academy / OLV Hospital – Bélgica
                        </p>}>
                        </PersonProfile>
                        <PersonProfile name="Dr. Plinio Pinto" photoUrl="https://teraquiz.s3.us-east-005.backblazeb2.com/teraquiz/image_1756394412485587852_68b073ac.webp" description={<p>Médico pela Universidade de Fortaleza.
                            <br /><br />
                            Urologista pela USP RP.
                            <br /><br />
                            Fellowship em Uro-oncologia e Cirurgia Robótica AC Camargo Câncer Center.
                            <br /><br />
                            Mestre em Ciências - USP.
                            Observership em uro-oncologia pelo Institute Mutualiste Montsouris - Paris, França.
                            Observership em cirurgia robótica Florida Hospital Celebration - Orlando, EUA.
                            <br /><br />
                            Médico Titular do Centro de Referência em Tumores Urológicos - AC Camargo Câncer Center.</p>}>

                        </PersonProfile>
                        <PersonProfile name="Dr. Gabriel Franco" photoUrl="https://teraquiz.s3.us-east-005.backblazeb2.com/teraquiz/image_1756394452752388512_68b073d4.webp" description={<p>Médico pela Universidade
                            Metropolitana de Santos
                            <br /><br />
                            Cirurgião Geral pela Pontifícia
                            <br /><br />
                            Universidade Católica de São Paulo
                            Urologista pelo Hospital Israelita Albert Einstein
                            <br /><br />
                            Pós graduação internacional em Cirurgia Robótica pelo Hospital Israelita Albert Einstein</p>}>

                        </PersonProfile>
                        <PersonProfile name="DR. Rafael Vilares" photoUrl="https://teraquiz.s3.us-east-005.backblazeb2.com/teraquiz/image_1756394486252065955_68b073f6.webp" description={<p>Médico pela Faculdade de Medicina da USP
                            <br /><br />
                            Cirurgia Geral pela Medicina USP
                            <br /><br />
                            Urologia pela Medicina USP
                            <br /><br />
                            Fellowship em Uropediatria na Medicina USP
                            <br /><br />
                            Observership no Hopital Femme-Mère-Enfant, Lyon em Uropediatria
                            <br /><br />
                            Observership no Hospital da Luz, Lisboa em Cirurgia Robótica
                        </p>}>
                        </PersonProfile>
                        <PersonProfile name="Dr. Rafael valente " photoUrl="https://teraquiz.s3.us-east-005.backblazeb2.com/teraquiz/image_1756394517107942284_68b07415.webp" description={<p>Médico pela Universidade Estadual do Ceará
                            <br /><br />
                            Cirurgião Geral pela USP RP
                            <br /><br />
                            Urologista pela USP RP
                            <br /><br />
                            Mestre em Ciências pela USP RP
                            <br /><br />
                            Fellowship em Uro-Oncologia pelo A.C. Camargo Câncer Center</p>}>

                        </PersonProfile>
                        <PersonProfile name="Dr. Sérgio DUARTE" photoUrl="https://teraquiz.s3.us-east-005.backblazeb2.com/teraquiz/image_1756394547211537430_68b07433.webp" description={<p> Médico pela Universidade de Brasília
                            <br /><br />
                            Urologista pela USP-SP
                            <br /><br />
                            TiSBU 2024 - 2 lugar</p>}>

                        </PersonProfile>
                        <PersonProfile name="DR. Igor Arantes Góes" photoUrl="https://teraquiz.s3.us-east-005.backblazeb2.com/teraquiz/image_1756394579615582600_68b07453.webp" description={<p>Médico pela Universidade São Francisco
                            <br /><br />
                            Cirurgião Geral pelo HUSF
                            <br /><br />
                            Urologista pelo HUSF
                            <br /><br />
                            Mestre em Ciências da saúde pela USF
                            <br /><br />
                            Doutorado em andamento  pela USF
                        </p>}>
                        </PersonProfile>
                    </div>
                </div>
            </div>
            <div className={`${background} py-12 px-8 text-white font-semibold text-lg md:text-xl text-center`}>
                <div className="max-w-4xl mx-auto">
                    <p className="mb-6">Materiais e referências: Diretrizes EAU, AUA e SBU, além de literatura-chave usada pelo TISBU</p>
                    <div className="w-24 h-1 bg-[#d2de00] mx-auto rounded-full"></div>
                </div>
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-white w-full py-16 md:py-24 px-4 md:px-8 flex flex-col items-center">
                <div className="text-center mb-12 md:mb-16">
                    <h2 className="text-4xl md:text-6xl font-extrabold bg-gradient-to-r from-[#08082f] via-gray-700 to-[#08082f] bg-clip-text text-transparent mb-4">
                        Módulos das Questões
                    </h2>
                    <p className="text-gray-600 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
                        9 áreas fundamentais da urologia organizadas para sua aprovação
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 w-full max-w-7xl">
                    <div className="relative bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 pt-10 border-l-4 border-[#d2de00]">
                        <div className="absolute -top-4 left-4 w-8 h-8 bg-gradient-to-br from-[#d2de00] to-[#a8b600] rounded-full flex items-center justify-center text-[#08082f] font-bold text-sm shadow-lg">
                            1
                        </div>
                        <h3 className="text-[#08082f] font-bold text-base md:text-lg leading-tight mb-4">Área 1: EMBRIOLOGIA, ANATOMIA, FISIOLOGIA E UROLOGIA GERAL</h3>
                    </div>
                    <div className="relative bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 pt-10 border-l-4 border-[#d2de00]">
                        <div className="absolute -top-4 left-4 w-8 h-8 bg-gradient-to-br from-[#d2de00] to-[#a8b600] rounded-full flex items-center justify-center text-[#08082f] font-bold text-sm shadow-lg">
                            2
                        </div>
                        <h3 className="text-[#08082f] font-bold text-base md:text-lg leading-tight mb-4">Área 2: TRAUMA, EMERGÊNCIAS E INFECÇÕES EM UROLOGIA</h3>
                    </div>
                    <div className="relative bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 pt-10 border-l-4 border-[#d2de00]">
                        <div className="absolute -top-4 left-4 w-8 h-8 bg-gradient-to-br from-[#d2de00] to-[#a8b600] rounded-full flex items-center justify-center text-[#08082f] font-bold text-sm shadow-lg">
                            3
                        </div>
                        <h3 className="text-[#08082f] font-bold text-base md:text-lg leading-tight mb-4">Área 3: ANDROLOGIA E INFERTILIDADE</h3>
                    </div>
                    <div className="relative bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 pt-10 border-l-4 border-[#d2de00]">
                        <div className="absolute -top-4 left-4 w-8 h-8 bg-gradient-to-br from-[#d2de00] to-[#a8b600] rounded-full flex items-center justify-center text-[#08082f] font-bold text-sm shadow-lg">
                            4
                        </div>
                        <h3 className="text-[#08082f] font-bold text-base md:text-lg leading-tight mb-4">Área 4: DISFUNÇÃO MICCIONAL E UROGINECOLOGIA</h3>
                    </div>
                    <div className="relative bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 pt-10 border-l-4 border-[#d2de00]">
                        <div className="absolute -top-4 left-4 w-8 h-8 bg-gradient-to-br from-[#d2de00] to-[#a8b600] rounded-full flex items-center justify-center text-[#08082f] font-bold text-sm shadow-lg">
                            5
                        </div>
                        <h3 className="text-[#08082f] font-bold text-base md:text-lg leading-tight mb-4">Área 5: UROONCOLOGIA</h3>
                    </div>
                    <div className="relative bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 pt-10 border-l-4 border-[#d2de00]">
                        <div className="absolute -top-4 left-4 w-8 h-8 bg-gradient-to-br from-[#d2de00] to-[#a8b600] rounded-full flex items-center justify-center text-[#08082f] font-bold text-sm shadow-lg">
                            6
                        </div>
                        <h3 className="text-[#08082f] font-bold text-base md:text-lg leading-tight mb-4">Área 6: ENDOUROLOGIA</h3>
                    </div>
                    <div className="relative bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 pt-10 border-l-4 border-[#d2de00]">
                        <div className="absolute -top-4 left-4 w-8 h-8 bg-gradient-to-br from-[#d2de00] to-[#a8b600] rounded-full flex items-center justify-center text-[#08082f] font-bold text-sm shadow-lg">
                            7
                        </div>
                        <h3 className="text-[#08082f] font-bold text-base md:text-lg leading-tight mb-4">Área 7: UROLOGIA RECONSTRUTORA</h3>
                    </div>
                    <div className="relative bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 pt-10 border-l-4 border-[#d2de00]">
                        <div className="absolute -top-4 left-4 w-8 h-8 bg-gradient-to-br from-[#d2de00] to-[#a8b600] rounded-full flex items-center justify-center text-[#08082f] font-bold text-sm shadow-lg">
                            8
                        </div>
                        <h3 className="text-[#08082f] font-bold text-base md:text-lg leading-tight mb-4">Área 8: TRANSPLANTE RENAL</h3>
                    </div>
                    <div className="relative bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 pt-10 border-l-4 border-[#d2de00]">
                        <div className="absolute -top-4 left-4 w-8 h-8 bg-gradient-to-br from-[#d2de00] to-[#a8b600] rounded-full flex items-center justify-center text-[#08082f] font-bold text-sm shadow-lg">
                            9
                        </div>
                        <h3 className="text-[#08082f] font-bold text-base md:text-lg leading-tight mb-4">Área 9: UROLOGIA INFANTIL</h3>
                    </div>
                </div>
                <InfoBox className="mt-16">
                    <p className={`${textVerdeOliva}`}>Metodologia:</p>
                    <div className="flex items-start gap-3">
                        <CheckIcon className={`w-8 h-8 ${textVerdeOliva} mt-1 flex-shrink-0`} />
                        <p>
                            Baseada em questões da banca
                        </p>
                    </div>
                    <div className="flex items-start gap-3">
                        <CheckIcon className={`w-8 h-8 ${textVerdeOliva} mt-1 flex-shrink-0`} />
                        <p className="font-light">
                            Respostas guiadas por recomendações dos principais guidelines internacionais
                        </p>
                    </div>
                    <div className="flex items-start gap-3">
                        <CheckIcon className={`w-8 h-8 ${textVerdeOliva} mt-1 flex-shrink-0`} />
                        <p>
                            Didática prática, objetiva e sem rodeios
                        </p>
                    </div>
                    <div className="flex items-start gap-3">
                        <CheckIcon className={`w-8 h-8 ${textVerdeOliva} mt-1 flex-shrink-0`} />
                        <p>
                            Materiais e referências: Diretrizes EAU, AUA e SBU, além de literatura-chave usada pelo TISBU.
                        </p>
                    </div>
                </InfoBox>
                <Button className="mt-8" onClick={scrollToPricing}>QUERO MINHA APROVAÇÃO!</Button>

                <div className="mt-16 text-center">
                    <h3 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-[#08082f] via-gray-700 to-[#08082f] bg-clip-text text-transparent mb-3">
                        Garantia & Segurança
                    </h3>
                    <p className="text-gray-600 text-lg mb-10 max-w-xl mx-auto">
                        Sua tranquilidade é nossa prioridade
                    </p>

                    <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="group bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 p-8 border border-gray-100 hover:border-[#d2de00]/30 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#d2de00] to-[#a8b600]"></div>
                            <div className="w-16 h-16 bg-gradient-to-br from-[#d2de00]/20 to-[#a8b600]/20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                                <div className="text-3xl">⚡</div>
                            </div>
                            <h4 className="text-xl font-bold text-[#08082f] mb-3">Acesso Instantâneo</h4>
                            <p className="text-gray-600 leading-relaxed">
                                Receba seu login imediatamente após a confirmação do pagamento. Comece a estudar em segundos!
                            </p>
                        </div>

                        <div className="group bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 p-8 border border-gray-100 hover:border-[#d2de00]/30 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#d2de00] to-[#a8b600]"></div>
                            <div className="w-16 h-16 bg-gradient-to-br from-[#d2de00]/20 to-[#a8b600]/20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                                <div className="text-3xl">🔒</div>
                            </div>
                            <h4 className="text-xl font-bold text-[#08082f] mb-3">100% Seguro</h4>
                            <p className="text-gray-600 leading-relaxed">
                                Pagamento protegido por criptografia SSL. Seus dados estão completamente seguros conosco.
                            </p>
                        </div>

                        <div className="group bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 p-8 border border-gray-100 hover:border-[#d2de00]/30 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#d2de00] to-[#a8b600]"></div>
                            <div className="w-16 h-16 bg-gradient-to-br from-[#d2de00]/20 to-[#a8b600]/20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                                <div className="text-3xl">💰</div>
                            </div>
                            <h4 className="text-xl font-bold text-[#08082f] mb-3">7 Dias de Garantia</h4>
                            <p className="text-gray-600 leading-relaxed">
                                Não gostou? Devolvemos 100% do seu dinheiro em até 7 dias. Sem perguntas, sem burocracia.
                            </p>
                        </div>
                    </div>

                    <div className="mt-10 p-6 bg-gradient-to-r from-[#d2de00]/10 to-[#a8b600]/10 rounded-xl border border-[#d2de00]/20 max-w-2xl mx-auto">
                        <p className="text-[#08082f] font-semibold text-lg">
                            🛡️ Sua aprovação protegida por nossa garantia de satisfação
                        </p>
                    </div>
                </div>

                <div className="mt-16 text-center">
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-8">
                        PARA QUEM É O BANCO?
                    </h3>
                    <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border-t-4 border-[#d2de00] group">
                            <div className="w-16 h-16 bg-gradient-to-br from-[#d2de00] to-[#a8b600] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                                <div className="text-2xl text-[#08082f] font-bold">👨‍⚕️</div>
                            </div>
                            <h4 className="text-lg font-bold text-[#08082f] mb-3">Médicos Urologistas</h4>
                            <p className="text-gray-600 leading-relaxed">
                                Médicos urologistas que buscam aprovação no TiSBU
                            </p>
                        </div>

                        <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border-t-4 border-[#d2de00] group">
                            <div className="w-16 h-16 bg-gradient-to-br from-[#d2de00] to-[#a8b600] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                                <div className="text-2xl text-[#08082f] font-bold">🎓</div>
                            </div>
                            <h4 className="text-lg font-bold text-[#08082f] mb-3">Residentes</h4>
                            <p className="text-gray-600 leading-relaxed">
                                Residentes de urologia em preparação para provas R1, R2 ou R3
                            </p>
                        </div>

                        <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border-t-4 border-[#d2de00] group">
                            <div className="w-16 h-16 bg-gradient-to-br from-[#d2de00] to-[#a8b600] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                                <div className="text-2xl text-[#08082f] font-bold">📚</div>
                            </div>
                            <h4 className="text-lg font-bold text-[#08082f] mb-3">Atualização Clínica</h4>
                            <p className="text-gray-600 leading-relaxed">
                                Médicos que desejam manter atualização clínica de alto nível
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div id="pricing-section" className={`${background} py-16 px-4 md:px-8`}>
                <div className="max-w-6xl mx-auto text-center">
                    <InfoBox className="mb-8">
                        <div className="text-center">
                            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                                BANCO DE QUESTÕES + CURSO INTENSIVO
                            </h2>
                            <p className="text-xl text-white mb-6">
                                1000 questões comentadas + 1000 Flashcards + Curso
                            </p>

                            <div className="flex items-center justify-center gap-4 mb-8">
                                <div className="text-center">
                                    <p className={`text-4xl font-bold ${textVerdeOliva}`}>R$ 3.870,00</p>
                                    <p className="text-white text-sm">até 12 vezes no cartão</p>
                                </div>
                            </div>
                        </div>

                        {/* <div className="border-t border-gray-600 pt-8 mt-8 opacity-80">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                                <div className="bg-gray-800/40 rounded-lg p-3">
                                    <p className="text-gray-400">CURSO 100% DIRECIONADO AO TISBU:</p>
                                    <p className="font-bold text-gray-400 line-through">R$ 3.790,00</p>
                                </div>
                                <div className="bg-gray-800/40 rounded-lg p-3">
                                    <p className="text-gray-400">1000 QUESTÕES COMENTADAS:</p>
                                    <p className="font-bold text-gray-400 line-through">R$ 2.499,00</p>
                                </div>
                                <div className="bg-gray-800/40 rounded-lg p-3">
                                    <p className="text-gray-400">1000 FLASHCARDS:</p>
                                    <p className="font-bold text-gray-400 line-through">R$ 599,00</p>
                                </div>
                                <div className="bg-gray-800/40 rounded-lg p-3">
                                    <p className="text-gray-400">CRIAÇÃO DE FLASHCARDS PRÓPRIOS:</p>
                                    <p className="font-bold text-gray-400 line-through">R$ 399,00</p>
                                </div>
                                <div className="bg-gray-800/40 rounded-lg p-3">
                                    <p className="text-gray-400">RESUMOS E MAPAS MENTAIS:</p>
                                    <p className="font-bold text-gray-400 line-through">R$ 299,00</p>
                                </div>
                                <div className="bg-gray-700/40 rounded-lg p-3">
                                    <p className="font-bold text-gray-400">VALOR TOTAL:</p>
                                    <p className="font-bold text-lg text-gray-400 line-through">R$ 7.586,00</p>
                                </div>
                            </div>
                        </div> */}

                        <div className="text-center mt-8 pt-8 border-t border-gray-600">
                            {/* <p className="text-white text-lg mb-2">
                                No preço de lançamento, até <span className={`font-bold ${textVerdeOliva}`}>10/09/2025</span>, você paga
                            </p>
                            <p className={`text-5xl font-extrabold ${textVerdeOliva} mb-4`}>
                                R$ 2.970,00
                            </p>
                            <p className="text-white text-lg mb-6">
                                por 1 ano de acesso.
                            </p>
                            <p className="text-gray-300 text-base mb-8">
                                Tudo o que for lançado nos próximos 12 meses já estará incluso.
                            </p> */}
                            <Button className="text-xl px-8 py-4" onClick={goToPurchase}>
                                Garantir meu acesso agora
                            </Button>
                        </div>
                    </InfoBox>
                </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white w-full py-16 px-4 md:px-8">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
                            DÚVIDAS E RESPOSTAS
                        </h2>
                    </div>

                    <div className="space-y-4">
                        {faqData.map((faq, index) => (
                            <FAQItem
                                key={index}
                                question={faq.question}
                                answer={faq.answer}
                                isOpen={openFAQs.includes(index)}
                                onToggle={() => toggleFAQ(index)}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <footer className={`${background} py-20 px-4 md:px-8 border-t-2 border-[#d2de00]/20`}>
                <div className="max-w-4xl mx-auto text-center">
                    <div className="mb-16">

                        <div className="flex items-center flex-col mb-4">
                            <img className="h-[44px]  mb-4" src="https://teraquiz.s3.us-east-005.backblazeb2.com/teraquiz/image_1756492611155244897_68b1f343.webp"></img>

                            <img className="h-[54px]" src="https://teraquiz.s3.us-east-005.backblazeb2.com/teraquiz/image_1756492433855137955_68b1f291.webp"></img>

                        </div>
                        <p className="text-gray-300 leading-relaxed text-xl mb-8 max-w-2xl mx-auto">
                            Sua plataforma de preparação para aprovação no TiSBU.
                            Questões premium e ensino de excelência.
                        </p>
                        <div className="flex justify-center gap-6 mb-12">
                            <a href="https://wa.me/5531996102844" className="group bg-gradient-to-br from-[#25D366]/20 to-[#25D366]/10 hover:from-[#25D366]/30 hover:to-[#25D366]/20 rounded-2xl px-8 py-4 transition-all duration-300 border border-[#25D366]/30 font-semibold text-white">
                                WhatsApp
                            </a>
                            <a href="https://www.instagram.com/uroteraquiz?igsh=MXU1OHBrZHhiNzhpbw%3D%3D&utm_source=qr" className="group bg-gradient-to-br from-[#E4405F]/20 to-[#E4405F]/10 hover:from-[#E4405F]/30 hover:to-[#E4405F]/20 rounded-2xl px-8 py-4 transition-all duration-300 border border-[#E4405F]/30 font-semibold text-white">
                                Instagram
                            </a>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
                        <div>
                            <h4 className="text-xl font-bold text-white mb-6 flex items-center justify-center gap-3">
                                <div className="w-2 h-2 bg-[#d2de00] rounded-full"></div>
                                Nossos Recursos
                            </h4>
                            <ul className="space-y-3 text-gray-300 text-lg">
                                <li className="hover:text-[#d2de00] cursor-pointer transition-colors">
                                    Curso Completo TiSBU
                                </li>
                                <li className="hover:text-[#d2de00] cursor-pointer transition-colors">
                                    1000 Questões Comentadas
                                </li>
                                <li className="hover:text-[#d2de00] cursor-pointer transition-colors">
                                    Flashcards Personalizados
                                </li>
                                <li className="hover:text-[#d2de00] cursor-pointer transition-colors">
                                    App Mobile Exclusivo
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-xl font-bold text-white mb-6 flex items-center justify-center gap-3">
                                <div className="w-2 h-2 bg-[#d2de00] rounded-full"></div>
                                Suas Garantias
                            </h4>
                            <div className="space-y-4">
                                <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-xl p-4 border border-gray-700/50 hover:border-[#d2de00]/50 transition-all duration-300">
                                    <h5 className="text-white font-bold text-lg mb-1">7 Dias de Garantia</h5>
                                    <p className="text-gray-300 text-sm">Devolução sem burocracia</p>
                                </div>

                                <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-xl p-4 border border-gray-700/50 hover:border-[#d2de00]/50 transition-all duration-300">
                                    <h5 className="text-white font-bold text-lg mb-1">Acesso Imediato</h5>
                                    <p className="text-gray-300 text-sm">Login liberado na hora</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="border-t-2 border-gray-800 pt-8">
                        <div className="bg-gradient-to-r from-gray-900/50 to-gray-800/50 rounded-2xl p-6">
                            <div className="text-gray-300">
                                <p className="text-lg font-semibold mb-2">© 2025 Teraquiz. Todos os direitos reservados.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Standard;